import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { BookOpen, ShieldCheck, ArrowRight, MapPin, Phone, Mail, Clock } from "lucide-react";
import { useSite } from "../components/SiteLayout";
import { fetchPublicCourses } from "../lib/site";

export default function SiteHome() {
  const site = useSite();
  const [courses, setCourses] = useState([]);
  useEffect(() => {
    fetchPublicCourses().then((c) => setCourses(c.slice(0, 3))).catch(() => {});
  }, []);

  const name = site.institute_name || "Lovely Institute";
  const sections = site.website_sections || {};

  return (
    <div data-testid="site-home">
      {/* hero */}
      <section className="bg-gradient-to-br from-blue-950 via-blue-900 to-blue-700 text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage:
              "radial-gradient(circle at 25% 30%, white 1px, transparent 1px), radial-gradient(circle at 70% 65%, white 1px, transparent 1px)",
            backgroundSize: "44px 44px, 70px 70px",
          }}
        />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28">
          <div className="max-w-2xl">
            <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight" data-testid="hero-heading">
              {site.hero_heading || `Welcome to ${name}`}
            </h1>
            <p className="mt-4 text-blue-100 text-base sm:text-lg leading-relaxed" data-testid="hero-intro">
              {site.hero_intro ||
                "Quality Education • Professional Courses • Recognized Certification"}
            </p>
            <div className="mt-8 flex flex-wrap gap-3">
              {sections.courses !== false && (
                <Link
                  to="/courses"
                  data-testid="hero-courses-btn"
                  className="inline-flex items-center gap-2 bg-white text-blue-900 font-semibold px-6 py-3 rounded-md hover:bg-blue-50 transition-soft"
                >
                  <BookOpen size={17} /> Explore Courses
                </Link>
              )}
              {sections.verification !== false && (
                <Link
                  to="/verify"
                  data-testid="hero-verify-btn"
                  className="inline-flex items-center gap-2 bg-white/10 border border-white/30 backdrop-blur font-semibold px-6 py-3 rounded-md hover:bg-white/20 transition-soft"
                >
                  <ShieldCheck size={17} /> Verify Certificate
                </Link>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* quick info strip */}
      {(site.address || site.phone || site.email || site.office_hours) && (
        <section className="border-b border-slate-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-slate-600">
            {site.address && (
              <div className="flex items-start gap-2.5">
                <MapPin size={17} className="text-blue-800 shrink-0 mt-0.5" />
                <span>{site.address}</span>
              </div>
            )}
            {site.phone && (
              <div className="flex items-start gap-2.5">
                <Phone size={17} className="text-blue-800 shrink-0 mt-0.5" />
                <span>{site.phone}{site.phone2 ? `  ·  ${site.phone2}` : ""}</span>
              </div>
            )}
            {site.email && (
              <div className="flex items-start gap-2.5">
                <Mail size={17} className="text-blue-800 shrink-0 mt-0.5" />
                <span>{site.email}</span>
              </div>
            )}
            {site.office_hours && (
              <div className="flex items-start gap-2.5">
                <Clock size={17} className="text-blue-800 shrink-0 mt-0.5" />
                <span>{site.office_hours}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {/* about snippet */}
      {site.about_text && sections.about !== false && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="max-w-3xl">
            <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold mb-2">About Us</div>
            <p className="text-slate-600 leading-relaxed whitespace-pre-line line-clamp-4">{site.about_text}</p>
            <Link to="/about" className="inline-flex items-center gap-1.5 text-blue-800 font-medium text-sm mt-3 hover:underline">
              Read more <ArrowRight size={14} />
            </Link>
          </div>
        </section>
      )}

      {/* popular courses */}
      {courses.length > 0 && sections.courses !== false && (
        <section className="bg-white border-y border-slate-200">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
            <div className="flex items-end justify-between mb-6">
              <div>
                <div className="text-xs uppercase tracking-widest text-blue-800 font-semibold mb-1">Our Courses</div>
                <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900">Popular Programs</h2>
              </div>
              <Link to="/courses" className="hidden sm:inline-flex items-center gap-1.5 text-blue-800 font-medium text-sm hover:underline">
                View all <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="home-courses">
              {courses.map((c) => (
                <Link
                  key={c.id}
                  to={`/courses/${c.id}`}
                  data-testid={`home-course-${c.id}`}
                  className="rounded-xl border border-slate-200 bg-slate-50 p-0 overflow-hidden hover:border-blue-300 hover:shadow-md transition-soft"
                >
                  {c.image ? (
                    <img src={c.image} alt={c.name} className="w-full h-28 object-cover" />
                  ) : (
                    <div className="w-full h-28 bg-gradient-to-br from-blue-900 to-blue-700 grid place-items-center text-white">
                      <BookOpen size={24} />
                    </div>
                  )}
                  <div className="p-5">
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-sm text-slate-500 mt-1">Duration: {c.duration}</div>
                  {c.course_type && (
                    <span className="inline-block mt-2 text-[11px] uppercase tracking-wide bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                      {c.course_type}
                    </span>
                  )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* verification CTA */}
      {sections.verification !== false && (
        <section className="max-w-6xl mx-auto px-4 sm:px-6 py-14">
          <div className="rounded-2xl bg-gradient-to-br from-blue-900 to-blue-700 text-white p-8 sm:p-12 flex flex-col sm:flex-row items-start sm:items-center gap-6">
            <div className="w-12 h-12 rounded-xl bg-white/10 grid place-items-center shrink-0">
              <ShieldCheck size={24} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl sm:text-2xl font-bold">Certificate Verification</h2>
              <p className="text-blue-100 text-sm mt-1.5 max-w-xl">
                Employers and institutions can verify any certificate or marksheet issued by{" "}
                {name} using the registration, enrollment or certificate number.
              </p>
            </div>
            <Link
              to="/verify"
              data-testid="home-verify-cta"
              className="inline-flex items-center gap-2 bg-white text-blue-900 font-semibold px-6 py-3 rounded-md hover:bg-blue-50 transition-soft shrink-0"
            >
              Verify Certificate <ArrowRight size={15} />
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
